import { Workflow, Download, Upload, ArrowUpToLine, ArrowDownToLine, ChevronRight, ChevronLeft, Database, Trash2, MessageSquare, Zap, Sparkles, Lightbulb, BookOpen, Code, Settings, User, LogOut, Bot } from 'lucide-react';
import { VERSION, API_URL, OYSTAR_RAG_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getUserRole } from '../auth/auth0-config';
import { useState, useEffect } from 'react';

interface FlowHeaderProps {
  flowName: string;
  onFlowNameChange: (name: string) => void;
  onSave: () => void;
  onExport: () => void;
  onLoad: () => void;
  onImport: (file: File) => void;
  onGenerate?: () => void;  // NEW: Genera da testo con LLM
  onPromptify?: () => void;  // NEW: Suggerisci prompt da obiettivo (Groq)
  onModify?: () => void;  // NEW: Modifica flow esistente con AI
  onGenerateAllCode?: () => void;  // NEW: Genera codice per tutti i nodi ACTION
  nodeCount: number;
  edgeCount: number;
  showProperties: boolean;
  onToggleProperties: () => void;
  hasMetadata?: boolean;
  onShowMetadata?: () => void;
  showPalette?: boolean;
  onTogglePalette?: () => void;
  onClearCanvas?: () => void;
  showSimulator?: boolean;
  onToggleSimulator?: () => void;
  onValidateSync?: () => void;
  showSettings?: boolean;
  onToggleSettings?: () => void;
  onShowCrawlHistory?: () => void;  // NEW: Mostra cronologia crawling
  onGenerateEmbed?: () => void;  // NEW: Genera codice embed
  onShowScheduler?: () => void;  // NEW: Mostra scheduler panel (n8n-style)
  onShowWebhook?: () => void;  // NEW: Mostra webhook panel (n8n-style)
  onShowCredentials?: () => void;  // NEW: Mostra credentials vault
  onShowExecutions?: () => void;  // NEW: Mostra execution history
  onShowTemplates?: () => void;  // NEW: Mostra template library
  onShowVersioning?: () => void;  // NEW: Mostra versioning panel
  onShowAIAssistant?: () => void;  // NEW: Mostra AI Assistant
  onShowWorkspaceSettings?: () => void;  // NEW: Mostra Workspace Settings
  onLogout?: () => void;  // Logout utente
}

export default function FlowHeader({ 
  flowName, 
  onFlowNameChange, 
  onSave, 
  onExport, 
  onLoad, 
  onImport,
  onGenerate,
  onPromptify,
  onModify,
  onGenerateAllCode,
  nodeCount, 
  edgeCount,
  showProperties,
  onToggleProperties,
  hasMetadata,
  onShowMetadata,
  showPalette,
  onTogglePalette,
  onClearCanvas,
  showSimulator,
  onToggleSimulator,
  onValidateSync,
  onShowCrawlHistory,
  onGenerateEmbed,
  onShowScheduler,
  onShowWebhook,
  onShowCredentials,
  onShowExecutions,
  onShowTemplates,
  onShowVersioning,
  onShowAIAssistant,
  onShowWorkspaceSettings,
  onLogout,
}: FlowHeaderProps) {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const userRole = getUserRole(user);
  
  // 🟢 Stato server (8001 e 8002)
  const [serverStatus, setServerStatus] = useState<{ backend: boolean; kb: boolean }>({ 
    backend: false, 
    kb: false 
  });

  // Controlla stato server ogni 5 secondi
  useEffect(() => {
    const checkServers = async () => {
      // Controlla backend (8001)
      let backendOk = false;
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000); // Timeout 2 secondi
        const backendResponse = await fetch(`${API_URL}/health`, { 
          method: 'GET',
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        backendOk = backendResponse.ok;
        console.log(`🔍 [Server Status] Backend (8001): ${backendOk ? '✅' : '❌'}`);
      } catch (error) {
        backendOk = false;
        console.error(`❌ [Server Status] Backend (8001) error:`, error);
      }

      // Controlla KB backend (8002)
      // In produzione, usa il proxy del backend (8001) per evitare mixed content
      let kbOk = false;
      const isProduction = typeof window !== 'undefined' && (window.location.protocol === 'https:' || window.location.hostname !== 'localhost');
      const isLocalhostKB = OYSTAR_RAG_URL.includes('localhost') || OYSTAR_RAG_URL.includes('127.0.0.1');
      
      if (isProduction && isLocalhostKB) {
        // In produzione, usa il proxy del backend (8001) per controllare il KB (8002)
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 2000); // Timeout 2 secondi
          const kbHealthResponse = await fetch(`${API_URL}/api/flow/kb/health-check`, { 
            method: 'GET',
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          if (kbHealthResponse.ok) {
            const healthData = await kbHealthResponse.json();
            kbOk = healthData.status === 'healthy';
            console.log(`🔍 [Server Status] KB (8002) via proxy: ${kbOk ? '✅' : '❌'}`, healthData);
          } else if (kbHealthResponse.status === 404) {
            // Endpoint non esiste ancora sulla VM - serve git pull
            kbOk = false;
            console.warn(`⚠️ [Server Status] KB health-check endpoint non trovato (404). Il backend sulla VM deve fare git pull e riavviare!`);
          } else {
            kbOk = false;
            console.error(`❌ [Server Status] KB health-check response not OK: ${kbHealthResponse.status}`);
          }
        } catch (error) {
          kbOk = false;
          console.error(`❌ [Server Status] KB (8002) via proxy error:`, error);
        }
      } else {
        // In sviluppo locale, controlla direttamente il KB backend
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 2000); // Timeout 2 secondi
          const kbResponse = await fetch(`${OYSTAR_RAG_URL}/api/health`, { 
            method: 'GET',
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          kbOk = kbResponse.ok;
          console.log(`🔍 [Server Status] KB (8002) direct: ${kbOk ? '✅' : '❌'}`);
        } catch (error) {
          kbOk = false;
          console.error(`❌ [Server Status] KB (8002) direct error:`, error);
        }
      }

      console.log(`📊 [Server Status] Final: Backend=${backendOk}, KB=${kbOk}`);
      setServerStatus({ backend: backendOk, kb: kbOk });
    };

    // Controlla immediatamente
    checkServers();

    // Poi ogni 5 secondi
    const interval = setInterval(checkServers, 5000);
    return () => clearInterval(interval);
  }, []);

  // 🟢 Stato complessivo: verde se entrambi attivi, rosso altrimenti
  // In produzione, considera solo il backend (KB su localhost non controllabile)
  const isProduction = typeof window !== 'undefined' && (window.location.protocol === 'https:' || window.location.hostname !== 'localhost');
  const isLocalhostKB = OYSTAR_RAG_URL.includes('localhost') || OYSTAR_RAG_URL.includes('127.0.0.1');
  const allServersUp = isProduction && isLocalhostKB 
    ? serverStatus.backend  // In produzione, considera solo backend
    : serverStatus.backend && serverStatus.kb;  // In sviluppo, entrambi
  
  // 🔵 Stato modal riavvio
  const [showRestartModal, setShowRestartModal] = useState(false);
  
  // 🔵 Funzione per riavviare server (mostra modal)
  const handleRestartServers = () => {
    setShowRestartModal(true);
  };
  
  // 🔵 Funzione per copiare comandi
  const copyCommand = (command: string) => {
    navigator.clipboard.writeText(command);
    alert('✅ Comando copiato negli appunti!');
  };
  
  // 🔵 Funzione per forzare controllo server
  const forceCheckServers = async () => {
    try {
      const backendController = new AbortController();
      const backendTimeout = setTimeout(() => backendController.abort(), 2000);
      const backendResponse = await fetch(`${API_URL}/health`, { 
        method: 'GET',
        signal: backendController.signal
      });
      clearTimeout(backendTimeout);
      const backendOk = backendResponse.ok;

      const kbController = new AbortController();
      const kbTimeout = setTimeout(() => kbController.abort(), 2000);
      const kbResponse = await fetch(`${OYSTAR_RAG_URL}/api/health`, { 
        method: 'GET',
        signal: kbController.signal
      });
      clearTimeout(kbTimeout);
      const kbOk = kbResponse.ok;

      setServerStatus({ backend: backendOk, kb: kbOk });
    } catch (error) {
      setServerStatus({ backend: false, kb: false });
    }
  };
  
  // Mappa ruoli a label e colori
  const roleConfig: Record<string, { label: string; color: string; bg: string }> = {
    'enterprise': { label: 'Enterprise', color: 'text-purple-700', bg: 'bg-purple-100' },
    'admin': { label: 'Admin', color: 'text-red-700', bg: 'bg-red-100' },
    'pro': { label: 'Pro', color: 'text-blue-700', bg: 'bg-blue-100' },
    'free': { label: 'Free', color: 'text-gray-700', bg: 'bg-gray-100' },
  };
  const roleInfo = roleConfig[userRole] || { label: userRole, color: 'text-gray-700', bg: 'bg-gray-100' };

  return (
    <div className="bg-white border-b-2 border-gray-200 shadow-md">
      {/* Riga 1: Logo, Nome, Stats */}
      <div className="px-4 py-2 flex items-center justify-between bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center gap-4">
          {/* Logo */}
          <div className="flex items-center">
            <div className="w-7 h-7 bg-purple-600 rounded-lg flex items-center justify-center mr-2">
              <Workflow className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-gray-900">
                OYSTAR.AI <span className="text-xs text-gray-400 font-normal">{VERSION}</span>
              </h1>
              <p className="text-xs text-gray-500">Flow Studio</p>
            </div>
          </div>

          {/* Nome Flow */}
          <div className="flex items-center gap-1 bg-white px-2 py-1 rounded border border-purple-200 shadow-sm"
               title="Modifica nome flow">
            <label className="text-xs font-bold text-purple-700">📝</label>
            <input
              type="text"
              value={flowName}
              onChange={(e) => onFlowNameChange(e.target.value)}
              placeholder="my-flow"
              className="px-2 py-0.5 border border-purple-300 rounded text-xs font-semibold focus:border-purple-600 focus:outline-none w-28 bg-purple-50"
            />
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 text-xs text-gray-600">
            <span><span className="font-bold">{nodeCount}</span> nodi</span>
            <span><span className="font-bold">{edgeCount}</span> rami</span>
            {/* 🟢 Indicatore stato server */}
            <div 
              className={`w-2 h-2 rounded-full ${allServersUp ? 'bg-green-500' : 'bg-red-500'} shadow-sm`}
              title={
                allServersUp 
                  ? `✅ Server attivi (Backend: ${serverStatus.backend ? '✅' : '❌'}, KB: ${serverStatus.kb ? '✅' : '❌'})`
                  : `❌ Server giù (Backend: ${serverStatus.backend ? '✅' : '❌'}, KB: ${serverStatus.kb ? '✅' : '❌'})`
              }
            />
            {/* 🔵 Pulsante riavvio server */}
            <button
              onClick={handleRestartServers}
              className="w-3 h-3 bg-blue-500 hover:bg-blue-600 rounded-sm shadow-sm transition-all cursor-pointer flex items-center justify-center"
              title="🔄 Clicca per vedere istruzioni riavvio server"
            >
              <span className="text-[8px] text-white font-bold">↻</span>
            </button>
          </div>
        </div>
        
        {/* 🔵 Modal Riavvio Server */}
        {showRestartModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">🔄 Riavvio Server</h2>
                <button
                  onClick={() => setShowRestartModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                {/* Rileva se siamo in produzione o sviluppo */}
                {(() => {
                  const isProd = typeof window !== 'undefined' && (window.location.protocol === 'https:' || window.location.hostname !== 'localhost');
                  return isProd ? (
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
                      <p className="text-blue-800 font-semibold mb-2">🌐 MODALITÀ PRODUZIONE</p>
                      <p className="text-blue-700 text-xs">
                        Stai usando <strong>{window.location.hostname}</strong> in produzione.
                        <br/>
                        I comandi devono essere eseguiti <strong>sulla VM/server remoto</strong> (via SSH).
                        <br/>
                        <span className="text-blue-600 font-semibold">Connettiti prima: <code className="bg-white px-1 rounded">ssh user@server</code></span>
                      </p>
                    </div>
                  ) : (
                    <div className="bg-green-50 p-3 rounded-lg border border-green-200 mb-4">
                      <p className="text-green-800 font-semibold mb-2">💻 MODALITÀ SVILUPPO LOCALE</p>
                      <p className="text-green-700 text-xs">
                        Stai usando l'applicazione in locale.
                        <br/>
                        I comandi devono essere eseguiti sul <strong>tuo Mac</strong>.
                      </p>
                    </div>
                  );
                })()}
                
                <p className="text-gray-700">
                  Per riavviare i server, esegui questi comandi in <strong>2 terminali separati</strong>:
                </p>
                
                {(() => {
                  // Rilevamento produzione: HTTPS O hostname diverso da localhost/127.0.0.1
                  const isProd = typeof window !== 'undefined' && (
                    window.location.protocol === 'https:' || 
                    (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1')
                  );
                  
                  if (isProd) {
                    // PRODUZIONE: Comandi per VM (NON Mac locale!)
                    return (
                      <>
                        <div className="bg-red-50 p-3 rounded-lg border-2 border-red-300 mb-3">
                          <p className="text-red-800 text-xs font-bold mb-2">🚨 ATTENZIONE: SEI IN PRODUZIONE!</p>
                          <p className="text-red-700 text-xs mb-2">
                            <strong>I comandi vanno eseguiti SULLA VM, NON sul tuo Mac!</strong>
                            <br/>
                            Connettiti prima alla VM: <code className="bg-white px-1 rounded font-bold">ssh fra@167.235.23.19</code>
                            <br/>
                            <span className="text-red-600">Poi esegui i comandi SULLA VM, non sul Mac!</span>
                          </p>
                        </div>
                        
                        <div className="bg-purple-50 p-3 rounded-lg border border-purple-200 mb-3">
                          <p className="text-purple-800 text-xs font-semibold mb-2">📥 STEP 0: Aggiorna codice dalla VM (esegui PRIMA):</p>
                          <p className="text-purple-700 text-xs mb-2">
                            Se hai fatto push dal Mac, aggiorna il codice sulla VM con git pull!
                          </p>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-bold text-purple-700 text-xs">GIT PULL (sulla VM):</h4>
                            <button
                              onClick={() => copyCommand('cd ~/pigna-studio && git pull')}
                              className="text-xs bg-purple-500 text-white px-2 py-1 rounded hover:bg-purple-600"
                            >
                              📋 Copia Pull
                            </button>
                          </div>
                          <code className="block bg-white p-2 rounded border text-xs font-mono">
                            cd ~/pigna-studio<br/>
                            git pull
                          </code>
                          <p className="text-gray-600 text-xs mt-2">
                            💡 Se ci sono conflitti, fai: <code className="bg-gray-100 px-1 rounded text-xs">git stash</code> poi <code className="bg-gray-100 px-1 rounded text-xs">git pull</code>
                          </p>
                        </div>
                        
                        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 mb-3">
                          <p className="text-yellow-800 text-xs font-semibold mb-2">⚠️ STEP 1: Kill processi esistenti (esegui SULLA VM):</p>
                          <p className="text-yellow-700 text-xs mb-2 font-semibold">
                            ⚠️ IMPORTANTE: Esegui questo comando SULLA VM dopo esserti connesso via SSH!
                            <br/>
                            <span className="text-red-600">Se vedi "Address already in use", esegui questo PRIMA!</span>
                          </p>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-bold text-yellow-700 text-xs">KILL PROCESSI (sulla VM):</h4>
                            <button
                              onClick={() => copyCommand('lsof -ti:8001 | xargs kill -9 2>/dev/null; lsof -ti:8002 | xargs kill -9 2>/dev/null; echo "✅ Processi killati"')}
                              className="text-xs bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600"
                            >
                              📋 Copia Kill
                            </button>
                          </div>
                          <code className="block bg-white p-2 rounded border text-xs font-mono">
                            lsof -ti:8001 | xargs kill -9 2&gt;/dev/null<br/>
                            lsof -ti:8002 | xargs kill -9 2&gt;/dev/null
                          </code>
                          <p className="text-gray-600 text-xs mt-2">
                            💡 Se il kill non funziona, verifica quale processo usa la porta:<br/>
                            <code className="bg-gray-100 px-1 rounded text-xs">lsof -i:8001</code> o <code className="bg-gray-100 px-1 rounded text-xs">lsof -i:8002</code>
                          </p>
                        </div>
                        
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-3">
                          <p className="text-blue-800 text-xs font-semibold mb-2">💡 STEP 2: Sulla VM i server sono gestiti con systemd</p>
                          <p className="text-blue-700 text-xs">
                            <strong>DOPO</strong> aver fatto git pull e killato i processi, riavvia i servizi.
                          </p>
                          <p className="text-blue-700 text-xs">
                            Usa i comandi systemctl per riavviare i servizi.
                          </p>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-bold text-blue-700">Backend (8001) - Riavvia service SULLA VM</h3>
                            <button
                              onClick={() => copyCommand('sudo systemctl restart pigna-flow')}
                              className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                            >
                              📋 Copia
                            </button>
                          </div>
                          <p className="text-red-600 text-xs mb-1 font-semibold">⚠️ Esegui SULLA VM, non sul Mac!</p>
                          <code className="block bg-white p-2 rounded border text-xs font-mono">
                            sudo systemctl restart pigna-flow
                          </code>
                          <p className="text-gray-600 text-xs mt-1">
                            (oppure: <code className="bg-gray-100 px-1 rounded">pigna-studio</code> se il service ha questo nome)
                          </p>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-bold text-green-700">KB Backend (8002) - Riavvia service SULLA VM</h3>
                            <button
                              onClick={() => copyCommand('sudo systemctl restart pigna-staging')}
                              className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                            >
                              📋 Copia
                            </button>
                          </div>
                          <p className="text-red-600 text-xs mb-1 font-semibold">⚠️ Esegui SULLA VM, non sul Mac!</p>
                          <code className="block bg-white p-2 rounded border text-xs font-mono">
                            sudo systemctl restart pigna-staging
                          </code>
                          <p className="text-gray-600 text-xs mt-1">
                            (oppure: <code className="bg-gray-100 px-1 rounded">pigna-2-staging</code> se il service ha questo nome)
                            <br/>
                            <span className="text-gray-500">Path sulla VM: <code className="bg-gray-100 px-1 rounded">/home/fra/pigna-rag</code></span>
                          </p>
                          <div className="bg-green-50 p-2 rounded border border-green-200 mt-2">
                            <p className="text-green-800 text-xs font-semibold mb-1">✅ Verifica che sia attivo:</p>
                            <code className="block bg-white p-1 rounded border text-xs font-mono">
                              sudo systemctl status pigna-staging<br/>
                              curl http://localhost:8002/api/health
                            </code>
                          </div>
                        </div>
                        
                        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                          <p className="text-yellow-800 text-xs font-semibold mb-2">🔍 STEP 3: Verifica stato servizi:</p>
                          <code className="block bg-white p-2 rounded border text-xs font-mono">
                            sudo systemctl status pigna-flow<br/>
                            sudo systemctl status pigna-staging
                          </code>
                        </div>
                      </>
                    );
                  } else {
                    // SVILUPPO LOCALE: Comandi per Mac locale
                    return (
                      <>
                        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 mb-3">
                          <p className="text-yellow-800 text-xs font-semibold mb-2">⚠️ IMPORTANTE: Killare processi esistenti prima di riavviare</p>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-bold text-yellow-700 text-xs">KILL PROCESSI (esegui PRIMA sul tuo Mac):</h4>
                            <button
                              onClick={() => copyCommand('lsof -ti:8001 | xargs kill -9 2>/dev/null; lsof -ti:8002 | xargs kill -9 2>/dev/null; echo "✅ Processi killati"')}
                              className="text-xs bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600"
                            >
                              📋 Copia Kill
                            </button>
                          </div>
                          <code className="block bg-white p-2 rounded border text-xs font-mono">
                            lsof -ti:8001 | xargs kill -9 2&gt;/dev/null<br/>
                            lsof -ti:8002 | xargs kill -9 2&gt;/dev/null
                          </code>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-bold text-blue-700">TERMINAL 1 - Backend (8001) sul tuo Mac</h3>
                            <button
                              onClick={() => copyCommand('cd /Users/fm/rag-simple/pigna-studio\n./start-backend-dev.sh')}
                              className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                            >
                              📋 Copia
                            </button>
                          </div>
                          <code className="block bg-white p-2 rounded border text-xs font-mono">
                            cd /Users/fm/rag-simple/pigna-studio<br/>
                            ./start-backend-dev.sh
                          </code>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-bold text-green-700">TERMINAL 2 - KB Backend (8002) sul tuo Mac</h3>
                            <button
                              onClick={() => copyCommand('cd /Users/fm/rag-simple\npython -m uvicorn backend.main:app --port 8002')}
                              className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                            >
                              📋 Copia
                            </button>
                          </div>
                          <code className="block bg-white p-2 rounded border text-xs font-mono">
                            cd /Users/fm/rag-simple<br/>
                            python -m uvicorn backend.main:app --port 8002
                          </code>
                          <p className="text-gray-600 text-xs mt-1">
                            <span className="text-gray-500">Nota: Sulla VM il path è <code className="bg-gray-100 px-1 rounded">/home/fra/pigna-rag</code></span>
                          </p>
                        </div>
                      </>
                    );
                  }
                })()}
                
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <p className="text-blue-800 text-xs">
                    💡 <strong>Nota:</strong> Il tondino diventerà <span className="bg-green-500 text-white px-1 rounded">🟢 verde</span> quando entrambi i server sono attivi.
                    <br/>
                    Lo stato viene controllato automaticamente ogni 5 secondi.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowRestartModal(false);
                    setTimeout(forceCheckServers, 3000);
                  }}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold transition-all"
                >
                  ✅ Ho riavviato i server
                </button>
                <button
                  onClick={() => setShowRestartModal(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold transition-all"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Right: User Info + Link Node Testing */}
        <div className="flex items-center gap-3">
          {/* User Info + Logout */}
          {user && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm">
                <User className="w-4 h-4 text-gray-500" />
                <div className="flex flex-col">
                  <span className="text-xs text-gray-700 font-medium truncate max-w-[150px]" title={user.email}>
                    {user.email}
                  </span>
                  <span className={`text-[10px] font-bold ${roleInfo.color} ${roleInfo.bg} px-1.5 py-0.5 rounded-full text-center`}>
                    {roleInfo.label}
                  </span>
                </div>
              </div>
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-all shadow-md hover:shadow-lg"
                  title="🚪 Logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
          
          {/* Link Node Testing */}
          <a
            href="/node-testing"
            onClick={(e) => {
              e.preventDefault();
              navigate('/node-testing');
            }}
            className="bg-gray-700 hover:bg-gray-800 text-white p-2 rounded-lg transition-all flex items-center gap-2 shadow-md hover:shadow-lg"
            title="🔧 Node Testing - Banco di prova per nodi LLM"
          >
            <Settings className="w-4 h-4" />
            <span className="text-xs font-semibold">Node Testing</span>
          </a>

          {/* Link Chat Embedded Tester */}
          <a
            href="/embed-tester"
            onClick={(e) => {
              e.preventDefault();
              navigate('/embed-tester');
            }}
            className="bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700 text-white p-2 rounded-lg transition-all flex items-center gap-2 shadow-md hover:shadow-lg"
            title="🖥️ Chat Embedded - Testa il widget chat come apparirà sui siti web"
          >
            <Code className="w-4 h-4" />
            <span className="text-xs font-semibold">Chat Embedded</span>
          </a>
        </div>
      </div>

      {/* Riga 2: AI Help + Funzionalità */}
      <div className="px-4 py-1.5 flex items-center gap-2 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-100">
        {/* AI Help - Primo e in evidenza */}
        {onShowAIAssistant && (
          <button
            onClick={onShowAIAssistant}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 shadow-lg hover:shadow-xl"
            title="🤖 AI Assistant - Chiedi aiuto per creare i tuoi flow"
          >
            <Bot className="w-5 h-5" />
            <span>AI Help</span>
          </button>
        )}

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Funzionalità principali */}
        {onShowScheduler && (
          <button
            onClick={onShowScheduler}
            className="bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
            title="⏰ Scheduler"
          >
            <span>⏰</span>
            <span>Scheduler</span>
          </button>
        )}

        {onShowWebhook && (
          <button
            onClick={onShowWebhook}
            className="bg-indigo-500 hover:bg-indigo-600 text-white px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
            title="🔗 Webhook"
          >
            <span>🔗</span>
            <span>Webhook</span>
          </button>
        )}

        {onShowCredentials && (
          <button
            onClick={onShowCredentials}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
            title="🔐 Vault"
          >
            <span>🔐</span>
            <span>Vault</span>
          </button>
        )}

        {onShowWorkspaceSettings && (
          <button
            onClick={onShowWorkspaceSettings}
            className="bg-slate-600 hover:bg-slate-700 text-white px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
            title="⚙️ Workspace Settings - Email, Branding"
          >
            <span>⚙️</span>
            <span>Settings</span>
          </button>
        )}

        {onShowExecutions && (
          <button
            onClick={onShowExecutions}
            className="bg-violet-500 hover:bg-violet-600 text-white px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
            title="📊 History"
          >
            <span>📊</span>
            <span>History</span>
          </button>
        )}

        {onShowTemplates && (
          <button
            onClick={onShowTemplates}
            className="bg-pink-500 hover:bg-pink-600 text-white px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
            title="📋 Templates"
          >
            <span>📋</span>
            <span>Templates</span>
          </button>
        )}

        {onShowVersioning && (
          <button
            onClick={onShowVersioning}
            className="bg-blue-500 hover:bg-blue-600 text-white px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
            title="🔄 Versions"
          >
            <span>🔄</span>
            <span>Versions</span>
          </button>
        )}

        {onGenerateEmbed && (
          <button
            onClick={onGenerateEmbed}
            className="bg-purple-500 hover:bg-purple-600 text-white px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
            title="💻 Embed"
          >
            <Code className="w-3.5 h-3.5" />
            <span>Embed</span>
          </button>
        )}
      </div>

      {/* Riga 3: File e Canvas */}
      <div className="px-4 py-1.5 flex items-center justify-between gap-2">
        {/* Left: File Operations */}
        <div className="flex items-center gap-1">
          {/* Toggle Palette */}
          {onTogglePalette && (
            <button
              onClick={onTogglePalette}
              className={`${showPalette ? 'bg-purple-500 hover:bg-purple-600' : 'bg-gray-400 hover:bg-gray-500'} text-white p-1.5 rounded transition flex items-center`}
              title={showPalette ? 'Minimizza palette' : 'Espandi palette'}
            >
              {showPalette ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          )}

          <div className="w-px h-5 bg-gray-300 mx-1" />

          {onGenerate && (
            <button
              onClick={onGenerate}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white p-1.5 rounded text-xs font-semibold transition flex items-center"
              title="Genera flow da testo"
            >
              <Sparkles className="w-3.5 h-3.5" />
            </button>
          )}

          {onPromptify && (
            <button
              onClick={onPromptify}
              className="bg-amber-500 hover:bg-amber-600 text-white p-1.5 rounded transition flex items-center"
              title="Suggerisci Prompt"
            >
              <Lightbulb className="w-3.5 h-3.5" />
            </button>
          )}

          {onModify && (
            <button
              onClick={onModify}
              className="bg-orange-500 hover:bg-orange-600 text-white p-1.5 rounded transition flex items-center"
              title="Modifica con AI"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}

          {onGenerateAllCode && (
            <button
              onClick={onGenerateAllCode}
              className="bg-cyan-500 hover:bg-cyan-600 text-white p-1.5 rounded transition flex items-center"
              title="Genera codice ACTION"
            >
              <Zap className="w-3.5 h-3.5" />
            </button>
          )}

          <div className="w-px h-5 bg-gray-300 mx-1" />

          <button
            onClick={onSave}
            className="bg-green-500 hover:bg-green-600 text-white p-1.5 rounded transition flex items-center"
            title="Salva"
          >
            <ArrowDownToLine className="w-3.5 h-3.5" />
          </button>

          <button
            onClick={onLoad}
            className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded transition flex items-center"
            title="Carica"
          >
            <ArrowUpToLine className="w-3.5 h-3.5" />
          </button>

          <button
            onClick={onExport}
            className="bg-emerald-500 hover:bg-emerald-600 text-white p-1.5 rounded transition flex items-center"
            title="Export JSON"
          >
            <Download className="w-3.5 h-3.5" />
          </button>

          <label className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded transition flex items-center" title="Import flow">
            <Upload className="w-3.5 h-3.5" />
            <input
              type="file"
              accept=".bpz,.json,.zip"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && onImport(e.target.files[0])}
            />
          </label>
        </div>

        {/* Right: UI Controls */}
        <div className="flex items-center gap-1">
          {onValidateSync && (
            <button
              onClick={onValidateSync}
              className="bg-yellow-500 hover:bg-yellow-600 text-white p-1.5 rounded transition flex items-center"
              title="Valida flow"
            >
              <Zap className="w-3.5 h-3.5" />
            </button>
          )}

          {onToggleSimulator && (
            <button
              onClick={onToggleSimulator}
              className={`${showSimulator ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-gray-500'} text-white px-2 py-1.5 rounded text-xs font-semibold transition flex items-center gap-1`}
              title={showSimulator ? 'Nascondi test' : 'Mostra test'}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span>▶️ Test</span>
            </button>
          )}

          {onShowCrawlHistory && (
            <button
              onClick={onShowCrawlHistory}
              className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded transition flex items-center"
              title="📚 Crawling"
            >
              <BookOpen className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            onClick={onToggleProperties}
            className={`${showProperties ? 'bg-purple-500 hover:bg-purple-600' : 'bg-gray-400'} text-white p-1.5 rounded transition`}
            title={showProperties ? 'Nascondi proprietà' : 'Mostra proprietà'}
          >
            {showProperties ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
          </button>

          {hasMetadata && onShowMetadata && (
            <button
              onClick={onShowMetadata}
              className="bg-purple-500 hover:bg-purple-600 text-white p-1.5 rounded transition flex items-center"
              title="Metadati"
            >
              <Database className="w-3.5 h-3.5" />
            </button>
          )}

          {onClearCanvas && (
            <button
              onClick={onClearCanvas}
              disabled={nodeCount === 0}
              className={`${nodeCount > 0 ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-300 cursor-not-allowed'} text-white p-1.5 rounded transition flex items-center`}
              title={nodeCount > 0 ? 'Pulisci canvas' : 'Canvas vuota'}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
